import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, ShieldCheck, Download } from "lucide-react";
import ProfileGate from "@/components/ProfileGate";

const money = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN");

const LABELS = {
  phone: "Mobile number", personal_email: "Personal email", dob: "Date of birth",
  address: "Address", emergency_contact_name: "Emergency contact name",
  emergency_contact_phone: "Emergency contact phone", pan: "PAN",
  account_holder_name: "Account holder name", bank_name: "Bank name",
  bank_account: "Bank account number", bank_ifsc: "IFSC", upi_id: "UPI ID",
};

export default function ConsultantPortalPage() {
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [statements, setStatements] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openStmt, setOpenStmt] = useState(null);
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Consult ids arrive as numbers in the 1mg sheet, so they stringify as
  // "4047672.0". Show them as plain ids.
  const cleanId = (v) => {
    const t = String(v == null ? "" : v);
    return (t.endsWith(".0") ? t.slice(0, -2) : t) || "—";
  };

  const openCases = async (st) => {
    if (openStmt && openStmt.id === st.id) { setOpenStmt(null); return; }
    setOpenStmt(st); setCases([]); setLoadingCases(true);
    try {
      const { data } = await api.get(`/consultant/me/statements/${st.id}/cases`);
      setCases(data.items || []);
    } catch (e) { setCases([]); } finally { setLoadingCases(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/consultant/me");
      setMe(data);
      const seed = {};
      (data.profile_fields || []).forEach((f) => { seed[f] = data[f] || ""; });
      setForm(seed);
      if (data.profile_complete) {
        const { data: st } = await api.get("/consultant/me/statements");
        setStatements(st || []);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/consultant/me", form);
      if (data.profile_complete) {
        toast.success("Details saved");
        setEditing(false);
        load();
      } else {
        toast.error("Still missing: " + data.missing.map((m) => LABELS[m] || m).join(", "));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!me) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const required = me.required_fields || [];
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-emerald-600 grid place-items-center">
              <Building2 className="h-4 w-4 text-white" strokeWidth={1.75} />
            </div>
            <span className="font-display font-bold">Arborvitae</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-zinc-600">{me.name}</span>
            <button onClick={logout} className="text-zinc-500 hover:underline">Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8 space-y-6">
        {!me.profile_complete ? (
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> Before we can pay you
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-600 mb-4">
                Please complete your details. Everything marked required is needed to make
                payment. Only you and the Arborvitae admin can see this.
              </p>
              <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
                {(me.profile_fields || []).map((f) => (
                  <div key={f}>
                    <Label className="text-xs text-zinc-500">
                      {LABELS[f] || f}{required.includes(f) ? " *" : ""}
                    </Label>
                    <Input value={form[f] || ""} onChange={set(f)} className="h-9"
                      type={f === "dob" ? "date" : "text"} data-testid={`con-field-${f}`} />
                  </div>
                ))}
                <div className="sm:col-span-2 flex justify-end">
                  <Button type="submit" disabled={busy} data-testid="con-save">
                    {busy ? "Saving…" : "Save details"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Your statements</h1>
              <p className="mt-1 text-sm text-zinc-500">
                Each month shows your consults and what is payable to you.
              </p>
            </div>
            <Card className="border-zinc-200 shadow-none"><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Period</TableHead><TableHead>Tele</TableHead>
                  <TableHead>Video</TableHead><TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">TDS</TableHead>
                    <TableHead className="text-right">Net payable</TableHead>
                  <TableHead className="text-right">Download</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {statements.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">
                        <button type="button" onClick={() => openCases(s)}
                          className="text-emerald-700 hover:underline">
                          {s.period_label || s.month}
                        </button>
                      </TableCell>
                      <TableCell>{s.tele}</TableCell><TableCell>{s.video}</TableCell>
                      <TableCell className="text-right">{money(s.amount)}</TableCell>
                      <TableCell className="text-right text-zinc-500">
                        {s.tds ? "-" + money(s.tds) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {money(s.net != null ? s.net : s.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <button className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
                          onClick={() => window.open(`/api/consultant/statements/${s.id}/download`, "_blank")}
                          data-testid={`con-dl-${s.id}`}>
                          <Download className="h-3.5 w-3.5" /> Excel
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!statements.length && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">
                      No statements yet. They appear once Arborvitae processes the month.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent></Card>

            {openStmt && (
              <Card className="border-zinc-200 shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium">
                      Cases for {openStmt.period_label || openStmt.month}
                    </div>
                    <button type="button" onClick={() => setOpenStmt(null)}
                      className="text-xs text-zinc-500 hover:underline">Close</button>
                  </div>
                  {loadingCases ? (
                    <p className="text-sm text-zinc-500">Loading your cases...</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow className="hover:bg-transparent">
                        <TableHead>#</TableHead>
                        <TableHead>Lead ID</TableHead>
                        <TableHead>Consult ID</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {cases.map((c, i) => (
                          <TableRow key={String(c.consult_id) + i}>
                            <TableCell className="text-zinc-500">{i + 1}</TableCell>
                            <TableCell className="font-mono text-xs">{cleanId(c.lead_id)}</TableCell>
                            <TableCell className="font-mono text-xs">{cleanId(c.consult_id)}</TableCell>
                            <TableCell>{c.kind === "tele" ? "Tele" : "Video"}</TableCell>
                            <TableCell className="text-right">{money(c.rate)}</TableCell>
                          </TableRow>
                        ))}
                        {!cases.length && (
                          <TableRow><TableCell colSpan={5} className="text-sm text-zinc-500">
                            No cases recorded for this month.
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-base font-semibold">Your payment details</CardTitle>
                  {!editing && (
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => setEditing(true)} data-testid="con-edit-profile">
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
                    {(me.profile_fields || []).map((f) => (
                      <div key={f}>
                        <Label className="text-xs text-zinc-500">
                          {LABELS[f] || f}{required.includes(f) ? " *" : ""}
                        </Label>
                        <Input value={form[f] || ""} onChange={set(f)} className="h-9"
                               type={f === "dob" ? "date" : "text"} />
                      </div>
                    ))}
                    <div className="sm:col-span-2 flex justify-end gap-2">
                      <Button type="button" variant="outline"
                              onClick={() => { load(); setEditing(false); }}
                              data-testid="con-cancel-profile">Cancel</Button>
                      <Button type="submit" disabled={busy} data-testid="con-update">
                        {busy ? "Saving\u2026" : "Update"}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                    {(me.profile_fields || []).map((f) => (
                      <div key={f}>
                        <dt className="text-xs text-zinc-500">{LABELS[f] || f}</dt>
                        <dd className="text-sm text-zinc-900 break-words">{form[f] || "\u2014"}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
      <ProfileGate me={me} endpoint="/consultant/me" onSaved={setMe} />
      </div>
  );
}
